import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { getEncumbranceTabName } from '@core/util/encumbrance';
import { Encumbrance } from '@models/encumbrance';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	EncumbrancesDispatch,
	MatterEncumbrancesState,
} from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs/internal/Observable';
import { filter, map, startWith, take } from 'rxjs/operators';
import { ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { CreateEncumbranceComponent } from './create-encumbrance/create-encumbrance.component';
import { StatementOfAccountsDispatch } from '@state/matter/statementOfAccounts';
import {
	StatementOfAccountDefaultTypes,
	TERAVIEW_FEE_TYPES,
} from '@models/statementOfAccount';

interface Tab {
	encumbranceId: string;
	name: string;
	url: string;
}

@UntilDestroy()
@Component({
	selector: 'app-encumbrances',
	templateUrl: './encumbrances.component.html',
	styleUrls: ['./encumbrances.component.scss'],
	standalone: false,
})
export class EncumbrancesComponent {
	@Select(MatterEncumbrancesState.getSorted) allEncumbrances$: Observable<
		Encumbrance[]
	>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	public createEncumbranceOpenRan = false;
	public ICON = ICON;
	public encumbrances: Encumbrance[];
	public tabs: Tab[] = [];
	public canAdd = true;

	constructor(
		private matDialog: MatDialog,
		private activatedRoute: ActivatedRoute,
		private navService: NavigationService,
		private encumbranceDispatch: EncumbrancesDispatch,
		private statementOfAccountsDispatch: StatementOfAccountsDispatch,
		private router: Router,
	) {
		this.router.events
			.pipe(
				untilDestroyed(this),
				filter((e) => e instanceof NavigationEnd),
				startWith(this.router),
				map(() => this.router.url),
				map(() => this.activatedRoute),
			)
			.subscribe((res) => {
				if (res.children.length === 0 && this.encumbrances?.length > 0) {
					this.navService.goToMatter_Mortgages_Encumbrances_Encumbrance(
						this.matterId,
						this.encumbrances[0].id,
					);
				}
			});

		this.allEncumbrances$
			.pipe(untilDestroyed(this))
			.subscribe((_encumbrances) => {
				this.encumbrances = _encumbrances;
				this.canAdd = _encumbrances?.length < 5;
				if (
					_encumbrances &&
					_encumbrances?.length === 0 &&
					!this.createEncumbranceOpenRan
				) {
					this.createEncumbranceOpenRan = true;
				}

				let _tabs;

				if (_encumbrances?.length > 0) {
					_tabs = _encumbrances.map((x) => ({
						encumbranceId: x.id,
						name: this.getName(x),
						url: UrlService.Matter_Encumbrances_Encumbrance(
							this.matterId,
							x.id,
						),
					}));

					if (this.activatedRoute.children.length === 0) {
						this.navService.goToMatter_Mortgages_Encumbrances_Encumbrance(
							this.matterId,
							_encumbrances[0].id,
						);
					}
				} else {
					_tabs = [];
				}
				this.tabs = _tabs;
			});
	}

	getName(encumbrance: Encumbrance) {
		const useShortName = true;
		return getEncumbranceTabName(encumbrance, useShortName);
	}

	addNew() {
		const createModal = this.matDialog.open(CreateEncumbranceComponent);

		createModal
			.afterClosed()
			.pipe(take(1))
			.subscribe((_newId) => {
				if (_newId) {
					this.navService.goToMatter_Mortgages_Encumbrances_Encumbrance(
						this.matterId,
						_newId,
					);
				}
			});
	}

	private deleteStatementOfAccounts(
		encumbranceId: string,
		types: string[],
	): void {
		types.forEach((type) => {
			// TODO: remove defaultType after the id once script is run
			this.statementOfAccountsDispatch.Delete(
				`${type}-${encumbranceId}-${StatementOfAccountDefaultTypes.REG_DISCHARGE_MORTGAGE}`,
			);
		});
	}

	deleteEncumbrance(encumbranceId: string): void {
		this.deleteStatementOfAccounts(encumbranceId, TERAVIEW_FEE_TYPES);
		this.encumbranceDispatch.Remove(encumbranceId);
		const newEncumbranceId = this.encumbrances.filter(
			(x) => x.id !== encumbranceId,
		)[0]?.id;
		if (newEncumbranceId) {
			this.navService.goToMatter_Mortgages_Encumbrances_Encumbrance(
				this.matterId,
				newEncumbranceId,
			);
		} else {
			this.navService.goToMatter_Mortgages_Encumbrances(this.matterId);
		}
	}

	trackById(index: number, item: Tab) {
		return item.encumbranceId;
	}
}
